import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Scale, ShieldCheck, Rocket, Users, Check, Gavel, Home, Briefcase, GitCompare,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ChatWidget } from "@/components/ChatWidget";

const fade = { hidden: { opacity: 0, y: 16 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const SEGMENTS = {
  consumers: {
    icon: Home,
    overline: "For individuals & consumers",
    h1: "Know your rights before you sign, dispute or decide.",
    sub: "Plain-English legal answers for everyday situations — tenancy, employment, contracts and consumer disputes. Legalis AI is on your side, finding every lawful angle in your favour.",
    cta: "Start free",
    code: "",
    benefits: [
      { icon: Scale, title: "Answers you can act on", desc: "Clear, jurisdiction-specific guidance with the exact rights and protections that apply to you." },
      { icon: ShieldCheck, title: "On your side", desc: "The Strategy agent hunts for defences, leverage and lawful options that strengthen your position." },
      { icon: Gavel, title: "Vetted lawyers when you need one", desc: "When a matter needs a professional, we connect you to vetted counsel — never spam." },
    ],
    routes: [
      { to: "/app/assistant", label: "Ask a legal question" },
      { to: "/app/summary", label: "Explain a contract in plain English" },
    ],
  },
  startups: {
    icon: Rocket,
    overline: "For startups & founders",
    h1: "Legal cover that scales as fast as you do.",
    sub: "From incorporation to your first hire, GDPR and market entry — get structured, source-based legal answers and a step-by-step roadmap for every jurisdiction you expand into.",
    cta: "Claim free Enterprise access",
    code: "STARTUP2026",
    benefits: [
      { icon: Rocket, title: "Market-entry roadmaps", desc: "A practical legal roadmap — entity, tax, employment, data, IP — with cost & timeline estimates and a downloadable diagram." },
      { icon: GitCompare, title: "Compare jurisdictions", desc: "See where to base, hire and sell — side-by-side, with the more favourable regime flagged." },
      { icon: ShieldCheck, title: "Compliance without a legal team", desc: "A prioritised compliance checklist for your exact business model, deadlines and penalties included." },
    ],
    routes: [
      { to: "/app/market-entry", label: "Build a market-entry roadmap" },
      { to: "/app/compliance", label: "Map my compliance obligations" },
    ],
  },
  "law-firms": {
    icon: Briefcase,
    overline: "For law firms",
    h1: "Win new matters from the clients you already have.",
    sub: "Give your clients free access to Legalis AI under your firm. Every question routes back to your lawyers — turning routine queries into qualified, billable matters, with a full referral inbox and analytics.",
    cta: "Talk to us",
    code: "",
    benefits: [
      { icon: Briefcase, title: "White-label referrals", desc: "Switch on \"own lawyers only\" — your workspace users are only ever referred to your firm, never competitors." },
      { icon: Users, title: "Client matter intake", desc: "Consultation requests land in your firm inbox with status tracking — no lead ever slips." },
      { icon: Gavel, title: "Your clause library & training", desc: "Curate your own clause library and steer the AI with firm-specific rules and answer styles." },
    ],
    routes: [
      { to: "/app/firm", label: "Set up my firm" },
      { to: "/app/billing", label: "See Law Firm plan" },
    ],
  },
};

export default function SegmentLanding() {
  const { segment } = useParams();
  const cfg = SEGMENTS[segment];
  if (!cfg) return <Navigate to="/" replace />;
  const Icon = cfg.icon;
  const registerTo = cfg.code ? `/register?code=${cfg.code}` : "/register";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/for/consumers" className={`hover:text-foreground transition-colors ${segment === "consumers" ? "text-foreground" : ""}`} data-testid="seg-nav-consumers">Consumers</Link>
            <Link to="/for/startups" className={`hover:text-foreground transition-colors ${segment === "startups" ? "text-foreground" : ""}`} data-testid="seg-nav-startups">Startups</Link>
            <Link to="/for/law-firms" className={`hover:text-foreground transition-colors ${segment === "law-firms" ? "text-foreground" : ""}`} data-testid="seg-nav-lawfirms">Law firms</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="rounded-none" data-testid="seg-login-btn">Sign in</Button></Link>
            <Link to={registerTo}><Button className="rounded-none" data-testid="seg-register-btn">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 relative">
          <motion.div variants={fade} initial="hidden" animate="show" className="max-w-3xl">
            <div className="flex items-center gap-2 overline text-brand mb-5">
              <Icon className="w-4 h-4" /> {cfg.overline}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="seg-hero-h1">
              {cfg.h1}
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{cfg.sub}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={registerTo}>
                <Button size="lg" className="rounded-none group" data-testid="seg-hero-cta">
                  {cfg.cta}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>
              <Link to="/"><Button size="lg" variant="outline" className="rounded-none" data-testid="seg-explore">Explore the platform</Button></Link>
            </div>
            {cfg.code && (
              <p className="mt-4 text-sm text-brand">Use code <span className="font-mono font-semibold">{cfg.code}</span> at signup for free Enterprise-level access — no card required.</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {cfg.benefits.map((b, i) => (
            <motion.div key={b.title} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="border border-border bg-card p-7" data-testid="seg-benefit">
              <div className="w-11 h-11 grid place-items-center border border-brand bg-brand/10 mb-4">
                <b.icon className="w-5 h-5 text-brand" strokeWidth={1.75} />
              </div>
              <h3 className="font-serif text-xl font-semibold">{b.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 border border-border bg-secondary/40 p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <div className="overline text-brand mb-2">Ready when you are</div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold">Start in minutes — {segment.replace("-", " ")} welcome.</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={registerTo}><Button size="lg" className="rounded-none" data-testid="seg-bottom-cta">{cfg.cta}</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-xs text-muted-foreground">Legalis AI — AI-generated legal information, not legal advice.</p>
        </div>
      </footer>
      <ChatWidget mode="landing" />
    </div>
  );
}
